/**
 * The `/organization` surface: an organization's own administrators managing it
 * (TECH-3200), against the `organization.*` procedures TECH-3199 landed.
 *
 * An independent surface, the way `/operations` is one: its own auth session (own
 * Firebase app, own persistence namespace), its own shell, and no link to it from
 * anywhere, so it is reached by typing the URL. It self-loads everything it needs,
 * assuming nothing about whether another surface ran first. The audience is the
 * reason. These are a customer's administrators, who usually also participate in
 * a workspace in the same tenant, so administering the organization and working
 * in it have to be two logins rather than one identity carrying administrative
 * reach into `/app`.
 *
 * The guard is a component with early returns, not a `beforeLoad`, and it awaits
 * its own probe so "still loading" and "not allowed" are distinguishable. A
 * redirect-based guard cannot tell them apart, and would land the user in a
 * different session's `/app`.
 */
import type { OrganizationSelf } from "@elaso/shared/types/organization";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { ulid } from "ulid";
import { useStore } from "zustand";
import { LoginCard } from "@/components/auth/login-card";
import { OrganizationNoAccess } from "@/components/organization/organization-no-access";
import { Button } from "@/components/ui/button";
import { useWsConnected } from "@/hooks/use-ws-connected";
import { claimSocket } from "@/lib/socket-owner";
import { orgAuthStore } from "@/lib/stores/auth-store";
import { ws } from "@/lib/websocket-wrapper";

export const Route = createFileRoute("/organization")({
	head: () => ({ meta: [{ title: "Organization - ELASO" }] }),
	component: RouteComponent,
});

function Centered({ label }: { label: string }) {
	return (
		<div className="flex h-full flex-col items-center justify-center gap-3">
			<Loader2 className="size-6 animate-spin text-muted-foreground" />
			<p className="text-muted-foreground">{label}</p>
		</div>
	);
}

function RouteComponent() {
	// The organization session, with its OWN Firebase app. Everything
	// identity-shaped here reads from it, so another user signed in elsewhere in
	// this browser has no bearing on this surface.
	const user = useStore(orgAuthStore, (s) => s.user);
	const initialized = useStore(orgAuthStore, (s) => s.initialized);
	const login = useStore(orgAuthStore, (s) => s.login);
	const logout = useStore(orgAuthStore, (s) => s.logout);
	const { isConnected, connectionEpoch } = useWsConnected();

	// `undefined` is "not asked yet", `null` is "asked, and the caller resolves to
	// no organization". Collapsing the two would render the no-access screen for a
	// moment on every load, which is the failure the awaited probe exists to avoid.
	const [self, setSelf] = useState<OrganizationSelf | null | undefined>(
		undefined
	);
	// The probe's own failure state, and the counter that re-runs it. Separate
	// from `self` because "the read failed" is not one of the answers `self`
	// carries.
	const [probeFailed, setProbeFailed] = useState(false);
	const [probeAttempt, setProbeAttempt] = useState(0);

	useEffect(() => {
		claimSocket("organization").catch((error) => {
			console.error("Failed to claim the socket for /organization:", error);
		});
	}, []);

	// Re-probed on every socket open, not only the first. `connectionEpoch` bumps
	// on reconnects the grace period hides, and authority can have been revoked
	// while the socket was down.
	useEffect(() => {
		if (!(isConnected && user)) return;
		let current = true;
		setProbeFailed(false);
		ws.sendWithPromise<OrganizationSelf | null>({
			id: ulid(),
			procedure: "organization.me",
			data: {},
		})
			.then((result) => {
				if (current) setSelf(result);
			})
			.catch((error) => {
				// Still `undefined`, so a failed read never ejects an administrator the
				// way collapsing it to `null` would. But it is RECORDED, because none
				// of this effect's other triggers fire while the socket stays up: with
				// only a log, one failed procedure left the surface spinning for good.
				console.error("Failed to resolve the caller's organization:", error);
				if (current) setProbeFailed(true);
			});
		return () => {
			current = false;
		};
	}, [isConnected, user, connectionEpoch, probeAttempt]);

	// The probe is about the signed-in account, so a sign-out must discard it.
	// Otherwise the next account inherits the previous one's answer until its own
	// arrives.
	useEffect(() => {
		if (!user) setSelf(undefined);
	}, [user]);

	if (!initialized) {
		return <Centered label="Loading..." />;
	}

	// This surface's own login, rendered IN PLACE. Not a redirect to `/`, which
	// would drop the deep link and sign into a different session entirely.
	if (!user) {
		return (
			<div className="flex h-full items-center justify-center p-4">
				<div className="w-full max-w-md">
					<LoginCard
						description="Sign in to manage your organization"
						onSubmit={login}
						sentryContext="org_auth_login"
						title="Organization"
					/>
				</div>
			</div>
		);
	}

	// Before the loading branch, or the spinner below would hide it.
	if (probeFailed && self === undefined) {
		return (
			<div className="flex h-full flex-col items-center justify-center gap-3">
				<p className="text-muted-foreground">
					Could not read your organization.
				</p>
				<Button
					onClick={() => setProbeAttempt((attempt) => attempt + 1)}
					size="sm"
					type="button"
					variant="outline"
				>
					Try again
				</Button>
			</div>
		);
	}

	if (!isConnected || self === undefined) {
		return <Centered label="Loading organization..." />;
	}

	// Signed in, but cannot administer an organization. Answered from DATA rather
	// than from a denial, which is what `organization.me` is for.
	if (!self?.isAdmin) {
		return (
			<OrganizationNoAccess
				email={user.email}
				onSignOut={logout}
				organizationName={self?.name ?? null}
			/>
		);
	}

	return (
		// The root layout gives this surface one full-height pane (see `isAppRoute`
		// in `__root.tsx`), so the body scrolls inside it rather than the page.
		<div className="flex h-full flex-col overflow-y-auto">
			<header className="flex shrink-0 items-center justify-between gap-4 border-b px-8 py-3">
				<div className="min-w-0">
					<div className="truncate font-medium">{self.name}</div>
					<div className="text-muted-foreground text-xs">
						{user.email} · {self.isOwner ? "Owner" : "Administrator"}
					</div>
				</div>
				<Button onClick={logout} size="sm" type="button" variant="outline">
					Sign out
				</Button>
			</header>
			<Outlet />
		</div>
	);
}
